import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SocialMediaButtons from '../components/SocialMediaButtons';
import Testimonials from '../components/Testimonials';
import FAQSection from '../components/FAQSection';
import StatsCounter from '../components/StatsCounter';

const CursoPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupSeen, setPopupSeen] = useState(() => {
    return localStorage.getItem('ofmPopupSeen') === 'true';
  });
  const [visibleSections, setVisibleSections] = useState(new Set());

  // Initialize countdown from localStorage or set to 20 minutes
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem('ofmCountdown');
    const savedTimestamp = localStorage.getItem('ofmCountdownTimestamp');
    
    if (saved && savedTimestamp) {
      const elapsed = Math.floor((Date.now() - parseInt(savedTimestamp)) / 1000);
      const remaining = parseInt(saved) - elapsed;
      return remaining > 0 ? remaining : 0;  // No reiniciar, dejar en 0
    }
    return 20 * 60; // 20 minutos en segundos
  });

  useEffect(() => {
    if (videoRef.current) {
      // Set slow motion effect (0.5 = half speed, 0.3 = very slow)
      videoRef.current.playbackRate = 0.6;
      
      const playVideo = async () => {
        try {
          await videoRef.current.play();
          console.log('Video background playing with slow effect');
        } catch (err) {
          console.log('Autoplay prevented:', err);
          setTimeout(async () => {
            try {
              await videoRef.current.play();
            } catch (e) {
              console.log('Second attempt failed');
            }
          }, 1000);
        }
      };
      
      playVideo();
    }
  }, []);

  // Show popup after 7 seconds if not seen before
  useEffect(() => {
    if (!popupSeen) {
      const popupTimer = setTimeout(() => {
        setShowPopup(true);
      }, 7000);
      
      return () => clearTimeout(popupTimer);
    }
  }, [popupSeen]);

  // Enhanced Intersection Observer for bidirectional scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const section = entry.target.dataset.section;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            // Add section when it enters viewport with at least 30% visible
            setTimeout(() => {
              setVisibleSections((prev) => {
                const newSet = new Set(prev);
                newSet.add(section);
                return newSet;
              });
            }, 100); // Small delay to prevent overlapping animations
          } else if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
            // Remove section when it leaves viewport (for re-animation)
            setVisibleSections((prev) => {
              const newSet = new Set(prev);
              newSet.delete(section);
              return newSet;
            });
          }
        });
      },
      { 
        threshold: [0.1, 0.3, 0.5],
        rootMargin: '0px' // No early trigger
      }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Countdown timer with localStorage persistence - STOPS AT 0
  useEffect(() => {
    // Save initial state
    localStorage.setItem('ofmCountdown', timeLeft.toString());
    localStorage.setItem('ofmCountdownTimestamp', Date.now().toString());

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        // STOP at 0, don't restart
        if (prevTime <= 0) {
          return 0;
        }
        
        const newTime = prevTime - 1;
        
        // Update localStorage
        localStorage.setItem('ofmCountdown', newTime.toString());
        localStorage.setItem('ofmCountdownTimestamp', Date.now().toString());
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupSeen(true);
    localStorage.setItem('ofmPopupSeen', 'true');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return {
      minutes: String(mins).padStart(2, '0'),
      seconds: String(secs).padStart(2, '0')
    };
  };

  const { minutes, seconds } = formatTime(timeLeft);

  const modules = [
    {
      number: 1,
      title: "Reclutamiento de modelos",
      description: "Scripts probados, dónde buscar, cómo contactar y cómo cerrar el trato"
    },
    {
      number: 2,
      title: "Manejo y gestión de modelos",
      description: "Cómo mantener motivadas a tus modelos, negociación de comisiones y estructura de pagos"
    },
    {
      number: 3,
      title: "Estrategia de contenido",
      description: "Qué tipo de contenido crear, frecuencia, tendencias y cómo posicionar a cada modelo"
    },
    {
      number: 4,
      title: "Ventas por chat (Chatting)",
      description: "El arte de vender por mensajes: técnicas, scripts de conversión y automatización"
    },
    {
      number: 5,
      title: "Marketing en redes sociales",
      description: "Cómo usar Instagram, TikTok, Twitter y Reddit para atraer suscriptores"
    },
    {
      number: 6,
      title: "Finanzas y comisiones",
      description: "Estructura de ingresos, reportes, manejo de dinero y escalamiento"
    },
    {
      number: 7,
      title: "Automatización y herramientas",
      description: "Software, bots de chatting, organización y productividad"
    },
    {
      number: 8,
      title: "Estructura de agencia",
      description: "Cómo armar tu equipo, roles, procesos y cultura de trabajo"
    },
    {
      number: 9,
      title: "Escalamiento",
      description: "Cómo pasar de 1-2 modelos a 5, 10, 20+ y multiplicar ingresos"
    },
    {
      number: 10,
      title: "Casos reales y mentoría",
      description: "Revisión de tu caso específico y resolución de problemas reales"
    }
  ];

  const benefits = [
    "Acceso a base de datos de modelos interesadas",
    "Scripts de reclutamiento probados",
    "Plantillas y documentos legales",
    "Grupo privado de networking",
    "Acompañamiento 1 a 1 durante y después del curso",
    "Actualizaciones gratuitas del contenido",
    "Certificado digital al finalizar",
    "Un sistema de trabajo profesional, escalable y automatizado",
    "Acompañamiento directo mío en cada paso"
  ];

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Curso OnlyFans - JGM University | Crea tu Agencia de Modelos OnlyFans</title>
        <meta name="title" content="Curso OnlyFans - JGM University | Crea tu Agencia de Modelos OnlyFans" />
        <meta name="description" content="Aprende a crear y escalar tu agencia de modelos OnlyFans. Curso completo con 10 módulos prácticos: reclutamiento, manejo, ventas, marketing y más. Acompañamiento 1 a 1." />
        <meta name="keywords" content="curso onlyfans, agencia onlyfans, curso ofm, agencia ofm, chatters onlyfans, manejo modelos onlyfans, curso agencia onlyfans, jgm university, capacitación onlyfans, ganar dinero onlyfans" />
        <link rel="canonical" href={window.location.href} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content="JGM University - Curso OnlyFans | Crea tu Agencia de Modelos" />
        <meta property="og:description" content="El único curso completo para crear y escalar tu agencia de modelos OnlyFans. 10 módulos prácticos con acompañamiento personalizado. Oferta especial 30% OFF." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/dyyu0c3k_Disen%CC%83o%20sin%20ti%CC%81tulo.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={window.location.href} />
        <meta property="twitter:title" content="JGM University - Curso OnlyFans | Crea tu Agencia" />
        <meta property="twitter:description" content="Curso completo para crear y escalar tu agencia de modelos OnlyFans. 10 módulos + acompañamiento 1 a 1." />
        <meta property="twitter:image" content="https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/dyyu0c3k_Disen%CC%83o%20sin%20ti%CC%81tulo.png" />
        
        {/* Structured Data - Course Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "JGM University - Curso Completo de Agencia OnlyFans",
            "description": "Aprende a crear y escalar tu agencia de modelos OnlyFans con 10 módulos prácticos: reclutamiento, manejo de modelos, estrategia de contenido, ventas por chat, marketing en redes sociales, finanzas y escalamiento.",
            "provider": {
              "@type": "Organization",
              "name": "JG Models Agency",
              "sameAs": window.location.origin
            },
            "offers": {
              "@type": "Offer",
              "category": "Paid",
              "price": "700",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "validFrom": new Date().toISOString()
            },
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "online",
              "duration": "P8H",
              "instructor": {
                "@type": "Person",
                "name": "JGM Agency"
              }
            },
            "educationalLevel": "Beginner to Advanced",
            "teaches": [
              "Reclutamiento de modelos OnlyFans",
              "Manejo y gestión de modelos",
              "Estrategia de contenido para OnlyFans",
              "Ventas por chat y chatting",
              "Marketing en redes sociales",
              "Finanzas y comisiones",
              "Automatización de procesos",
              "Estructura de agencia",
              "Escalamiento de negocio"
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black relative">
        {/* Background Video with Overlay */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-black/80 z-10" />
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          className="w-full h-full object-cover"
          preload="auto"
        >
          <source src="https://customer-assets.emergentagent.com/job_b3a752a4-a88d-4596-9eab-3e5ad633dd0d/artifacts/p96bnntb_anonimostory.com_Instagram_favoritecol_3754198994303165247_18573287986.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Content Container */}
      <div className="relative z-20">
      {/* Header with Back Button and Countdown (after popup seen) */}
      <header className="bg-black/40 backdrop-blur-sm p-4 md:p-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="text-gray-300 hover:text-white transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Volver
            </button>
            
            {/* Countdown Timer - Shows after popup is seen */}
            {popupSeen && timeLeft > 0 && (
              <div className="flex-1 flex justify-center">
                <div className="bg-black/70 backdrop-blur-sm border border-[#c8784e] rounded-lg px-3 md:px-4 py-1.5 md:py-2 flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-300 font-medium">
                    Oferta termina:
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="bg-[#c8784e]/30 rounded px-1.5 md:px-2 py-0.5">
                      <span className="text-sm md:text-base font-bold text-[#c8784e]">
                        {minutes}
                      </span>
                    </div>
                    <span className="text-sm md:text-base font-bold text-[#c8784e]">:</span>
                    <div className="bg-[#c8784e]/30 rounded px-1.5 md:px-2 py-0.5">
                      <span className="text-sm md:text-base font-bold text-[#c8784e]">
                        {seconds}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show "Oferta Expirada" when countdown reaches 0 */}
            {popupSeen && timeLeft === 0 && (
              <div className="flex-1 flex justify-center">
                <div className="bg-red-900/70 backdrop-blur-sm border border-red-500 rounded-lg px-3 md:px-4 py-1.5 md:py-2">
                  <span className="text-xs md:text-sm text-white font-bold">
                    ⏰ Oferta Expirada
                  </span>
                </div>
              </div>
            )}
            
            <img 
              src="https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/dyyu0c3k_Disen%CC%83o%20sin%20ti%CC%81tulo.png" 
              alt="JG Models Agency" 
              className="h-10 md:h-16 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 md:py-20 px-4" data-section="hero">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block mb-6 md:mb-8 animate-slide-up">
            <img 
              src="https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/dyyu0c3k_Disen%CC%83o%20sin%20ti%CC%81tulo.png" 
              alt="JG Models Agency" 
              className="h-24 md:h-32 lg:h-40 w-auto mx-auto"
            />
          </div>
          
          <div className={`space-y-4 md:space-y-6 ${visibleSections.has('hero') ? 'section-visible' : 'section-hidden'}`}>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              <span className="text-white">JGM</span>{' '}
              <span className="text-[#c8784e]">University</span>
            </h1>
            
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
              El único curso completo para crear y escalar tu agencia de modelos OnlyFans
            </p>
            
            <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto">
              Aprende todo lo necesario para gestionar modelos, generar ingresos consistentes y construir una agencia profesional desde cero.
            </p>
            
            <button 
              onClick={() => window.open('https://wa.link/0wlub8', '_blank')}
              className="bg-[#c8784e] text-black px-6 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-sm md:text-lg hover:bg-[#d89a6f] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#c8784e]/50 mt-4 md:mt-6"
            >
              Quiero más información
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsCounter type="university" />

      {/* What You'll Learn Section */}
      <section className="py-12 md:py-20 px-4" data-section="modules">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-8 md:mb-12 ${visibleSections.has('modules') ? 'section-visible' : 'section-hidden'}`}>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4">
              ¿Qué aprenderás?
            </h2>
            <p className="text-sm md:text-base text-gray-400">
              10 módulos completos que cubren todo lo necesario
            </p>
          </div>

          <div className={`grid md:grid-cols-2 gap-4 md:gap-6 ${visibleSections.has('modules') ? 'section-visible' : 'section-hidden'}`}>
            {modules.map((module, index) => (
              <div 
                key={index}
                className="bg-black/60 backdrop-blur-sm border border-[#c8784e]/30 rounded-xl p-5 md:p-6 hover:border-[#c8784e] hover:bg-black/70 transition-all duration-300 hover:shadow-lg hover:shadow-[#c8784e]/20"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-[#c8784e]/20 text-[#c8784e] w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0">
                    {module.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-bold text-white mb-2">
                      {module.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-400 leading-relaxed">
                      {module.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 px-4 bg-black/20" data-section="benefits">
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-8 md:mb-12 ${visibleSections.has('benefits') ? 'section-visible' : 'section-hidden'}`}>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4">
              ¿Qué incluye el curso?
            </h2>
            <p className="text-sm md:text-base text-gray-400">
              Todo lo que necesitas para triunfar
            </p>
          </div>

          <div className={`space-y-3 md:space-y-4 ${visibleSections.has('benefits') ? 'section-visible' : 'section-hidden'}`}>
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-black/60 backdrop-blur-sm border border-[#c8784e]/30 rounded-lg p-4 md:p-5 flex items-start gap-3 md:gap-4 hover:border-[#c8784e] hover:bg-black/70 transition-all duration-300"
              >
                <svg className="w-6 h-6 text-[#c8784e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-sm md:text-base text-gray-300 flex-1">
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Oferta Especial Section */}
      <section className="py-12 md:py-20 px-4" data-section="oferta">
        <div className="max-w-4xl mx-auto">
          <div className={`bg-black/85 backdrop-blur-sm border-2 border-[#c8784e] rounded-2xl p-6 md:p-10 relative overflow-hidden ${visibleSections.has('oferta') ? 'section-visible' : 'section-hidden'}`}>
            {/* Badge de descuento */}
            <div className="absolute top-4 right-4 md:top-6 md:right-6">
              <div className="bg-[#c8784e] text-black px-4 py-2 md:px-6 md:py-3 rounded-full font-bold text-lg md:text-2xl transform rotate-12 shadow-lg">
                30% OFF
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4">
                🔥 Oferta Especial por Tiempo Limitado
              </h2>
              <p className="text-sm md:text-lg text-gray-300 mb-6 md:mb-8">
                Aprovecha este descuento exclusivo ahora
              </p>

              {/* Countdown Timer */}
              {timeLeft > 0 && (
                <div className="mb-6 md:mb-8">
                  <p className="text-sm md:text-base text-gray-400 mb-3 md:mb-4">
                    La oferta termina en:
                  </p>
                  <div className="flex justify-center gap-3 md:gap-4">
                    <div className="bg-[#c8784e]/20 border-2 border-[#c8784e] rounded-lg md:rounded-xl p-3 md:p-4 min-w-[70px] md:min-w-[100px]">
                      <div className="text-3xl md:text-5xl font-bold text-[#c8784e]">
                        {minutes}
                      </div>
                      <div className="text-xs md:text-sm text-gray-400 mt-1">
                        MINUTOS
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-3xl md:text-5xl font-bold text-[#c8784e]">:</span>
                    </div>
                    <div className="bg-[#c8784e]/20 border-2 border-[#c8784e] rounded-lg md:rounded-xl p-3 md:p-4 min-w-[70px] md:min-w-[100px]">
                      <div className="text-3xl md:text-5xl font-bold text-[#c8784e]">
                        {seconds}
                      </div>
                      <div className="text-xs md:text-sm text-gray-400 mt-1">
                        SEGUNDOS
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Expired Message */}
              {timeLeft === 0 && (
                <div className="mb-6 md:mb-8">
                  <div className="bg-red-900/20 border-2 border-red-500 rounded-xl p-4 md:p-6">
                    <p className="text-xl md:text-2xl font-bold text-red-400 mb-2">
                      ⏰ La oferta ha expirado
                    </p>
                    <p className="text-sm md:text-base text-gray-300">
                      Contáctanos para consultar disponibilidad
                    </p>
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center justify-center gap-3 md:gap-4 mb-3">
                  <span className="text-2xl md:text-3xl text-gray-500 line-through">
                    $1,000 USD
                  </span>
                  <span className="text-4xl md:text-6xl font-bold text-[#c8784e]">
                    $700 USD
                  </span>
                </div>
                <p className="text-xs md:text-sm text-gray-400 mb-4">
                  Ahorra $300 USD inscribiéndote ahora
                </p>

                {/* Payment & Duration Info */}
                <div className="bg-[#c8784e]/10 border border-[#c8784e]/30 rounded-lg p-4 md:p-5 mb-4">
                  <div className="grid md:grid-cols-2 gap-3 md:gap-4 text-left">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#c8784e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <div>
                        <h4 className="text-sm md:text-base font-bold text-white mb-1">
                          Pago Flexible
                        </h4>
                        <p className="text-xs md:text-sm text-gray-300">
                          Puedes pagar en cuotas, una por cada clase
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-[#c8784e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="text-sm md:text-base font-bold text-white mb-1">
                          Duración del Curso
                        </h4>
                        <p className="text-xs md:text-sm text-gray-300">
                          4 sesiones intensivas de 2 horas cada una
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button 
                onClick={() => window.open('https://wa.link/0wlub8', '_blank')}
                className="bg-[#c8784e] text-black px-8 md:px-12 py-4 md:py-5 rounded-lg md:rounded-xl font-bold text-base md:text-xl hover:bg-[#d89a6f] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#c8784e]/50 animate-pulse"
              >
                Quiero aprovechar esta oferta
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials type="university" />

      {/* FAQ Section */}
      <FAQSection type="university" />

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            ¿Listo para transformar tu carrera?
          </h2>
          <p className="text-sm md:text-lg text-gray-400 mb-6 md:mb-8">
            Únete a JGM University y comienza a construir tu agencia profesional hoy mismo
          </p>
          <button 
            onClick={() => window.open('https://wa.link/0wlub8', '_blank')}
            className="bg-white text-black px-6 md:px-10 py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-sm md:text-lg hover:bg-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            Contactar por WhatsApp
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 md:py-8 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          {/* Social Media */}
          <div className="mb-6">
            <p className="text-center text-sm text-gray-400 mb-4">Síguenos en nuestras redes</p>
            <SocialMediaButtons variant="fixed" adminWhatsApp={true} brandColors={true} />
          </div>
          
          {/* Copyright */}
          <div className="text-center">
            <p className="text-xs md:text-sm text-gray-500">
              © 2024 JG Models Agency - JGM University. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Social Media Buttons */}
      <SocialMediaButtons variant="floating" brandColors={true} />
      </div>
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-black border-2 border-[#c8784e] rounded-2xl max-w-lg w-full p-6 md:p-8 relative animate-scale-in shadow-2xl shadow-[#c8784e]/50">
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div className="bg-[#c8784e] text-black px-6 py-2 rounded-full font-bold text-xl inline-block mb-4 transform -rotate-3">
                30% OFF
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                🔥 Oferta Exclusiva
              </h3>
              
              <p className="text-gray-300 mb-6">
                Aprovecha este descuento especial por tiempo limitado
              </p>

              <div className="bg-[#c8784e]/10 border border-[#c8784e]/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <span className="text-xl text-gray-500 line-through">$1,000</span>
                  <span className="text-4xl font-bold text-[#c8784e]">$700</span>
                </div>
                <p className="text-sm text-gray-400">Ahorra $300 USD</p>
              </div>

              <button
                onClick={() => {
                  handleClosePopup();
                  window.open('https://wa.link/0wlub8', '_blank');
                }}
                className="w-full bg-[#c8784e] text-black py-4 rounded-lg font-bold text-lg hover:bg-[#d89a6f] transition-all duration-300 hover:scale-105 mb-3"
              >
                Quiero aprovechar esta oferta
              </button>

              <button
                onClick={handleClosePopup}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CursoPage;